// Compact buy-box helpers. Neither element owns variant state: <variant-selects>
// stays the source of truth and these only mirror or drive it.

const readJson = (el, fallback) => {
  if (!el) return fallback;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return fallback;
  }
};

if (!customElements.get('option-dropdowns')) {
  class OptionDropdowns extends HTMLElement {
    connectedCallback() {
      this.picker = document.getElementById(`variant-selects-${this.dataset.section}`);
      if (!this.picker) return;

      this.variants = readJson(this.querySelector('[data-option-dropdowns-variants]'), []);
      this.dropdowns = [...this.querySelectorAll('.option-dropdown')];
      this.typeahead = '';

      this.onPickerChange = () => this.sync();
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };

      for (const dropdown of this.dropdowns) {
        const field = dropdown.querySelector('.option-dropdown__field');
        const list = dropdown.querySelector('.option-dropdown__list');
        field.addEventListener('click', () => (this.openDropdown === dropdown ? this.close() : this.open(dropdown)));
        field.addEventListener('keydown', (event) => this.onFieldKeydown(event, dropdown));
        list.addEventListener('keydown', (event) => this.onListKeydown(event, dropdown));
        list.addEventListener('click', (event) => {
          const option = event.target.closest('[role="option"]');
          if (option) this.choose(dropdown, option);
        });
      }

      this.picker.addEventListener('change', this.onPickerChange);
      document.addEventListener('click', this.onDocumentClick);
      this.sync();
    }

    disconnectedCallback() {
      this.picker?.removeEventListener('change', this.onPickerChange);
      document.removeEventListener('click', this.onDocumentClick);
    }

    // Mirrors VariantSelects.updateOptions so both read the same selection.
    selectedOptions() {
      return [...this.picker.querySelectorAll('select, fieldset')].map((el) =>
        el.tagName === 'SELECT' ? el.value : el.querySelector('input:checked')?.value
      );
    }

    // Same cascade as the native picker: a value's state depends on the options
    // chosen before it, and aggregates every combination after it.
    statusFor(index, value, selected) {
      const matches = this.variants.filter(
        ({ options }) => options[index] === value && selected.slice(0, index).every((v, i) => options[i] === v)
      );
      if (!matches.length) return 'unavailable';

      const available = matches.filter((variant) => variant.available);
      if (!available.length) return 'soldout';

      const inStock = available.filter((variant) => !variant.pre);
      if (!inStock.length) return 'preorder';
      if (inStock.some((variant) => variant.low === 0)) return 'instock';

      const units = inStock.reduce((total, variant) => total + variant.low, 0);
      return units <= 4 ? 'low' : 'instock';
    }

    sync() {
      const selected = this.selectedOptions();
      const labels = {
        low: 'Low stock',
        soldout: 'Notify me',
        preorder: 'Pre-order',
      };
      const spoken = {
        ...labels,
        soldout: window.variantStrings?.soldOut ?? 'Sold out',
        unavailable: window.variantStrings?.unavailable ?? 'Unavailable',
      };

      for (const dropdown of this.dropdowns) {
        const index = Number(dropdown.dataset.optionPosition) - 1;
        const current = selected[index];
        dropdown.querySelector('[data-option-dropdown-value]').textContent = current ?? '';

        for (const option of dropdown.querySelectorAll('[role="option"]')) {
          const status = this.statusFor(index, option.dataset.value, selected);
          option.dataset.status = status;
          option.setAttribute('aria-selected', option.dataset.value === current);
          option.querySelector('[data-option-dropdown-status]').textContent = labels[status] ?? '';
          option.setAttribute('aria-label', [option.dataset.value, spoken[status]].filter(Boolean).join(', '));
        }
      }
    }

    options(dropdown) {
      return [...dropdown.querySelectorAll('[role="option"]')];
    }

    open(dropdown) {
      if (this.openDropdown && this.openDropdown !== dropdown) this.close();
      const field = dropdown.querySelector('.option-dropdown__field');
      const list = dropdown.querySelector('.option-dropdown__list');
      this.openDropdown = dropdown;
      dropdown.classList.add('option-dropdown--open');
      field.setAttribute('aria-expanded', 'true');
      list.hidden = false;
      const options = this.options(dropdown);
      this.setActive(dropdown, options.find((option) => option.getAttribute('aria-selected') === 'true') ?? options[0]);
      list.focus({ preventScroll: true });
    }

    close({ focusField = false } = {}) {
      const dropdown = this.openDropdown;
      if (!dropdown) return;
      const field = dropdown.querySelector('.option-dropdown__field');
      const list = dropdown.querySelector('.option-dropdown__list');
      this.openDropdown = null;
      dropdown.classList.remove('option-dropdown--open');
      field.setAttribute('aria-expanded', 'false');
      list.hidden = true;
      list.removeAttribute('aria-activedescendant');
      if (focusField) field.focus();
    }

    setActive(dropdown, option) {
      if (!option) return;
      const list = dropdown.querySelector('.option-dropdown__list');
      for (const item of this.options(dropdown)) item.classList.toggle('option-dropdown__option--active', item === option);
      list.setAttribute('aria-activedescendant', option.id);
      const { offsetTop, offsetHeight } = option;
      if (offsetTop < list.scrollTop) list.scrollTop = offsetTop;
      else if (offsetTop + offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTop = offsetTop + offsetHeight - list.clientHeight;
      }
    }

    moveActive(dropdown, step) {
      const options = this.options(dropdown);
      const current = options.findIndex((option) => option.classList.contains('option-dropdown__option--active'));
      const next = step === Infinity ? options.length - 1 : step === -Infinity ? 0 : current + step;
      this.setActive(dropdown, options[Math.min(Math.max(next, 0), options.length - 1)]);
    }

    choose(dropdown, option) {
      const { optionName } = dropdown.dataset;
      const radio = [...this.picker.querySelectorAll('input[type="radio"]')].find(
        (input) => input.name === optionName && input.value === option.dataset.value
      );
      this.close({ focusField: true });
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Enter and Space reach the field's click handler natively (Space only on
    // keyup), so handling them here as well would open and immediately close.
    onFieldKeydown(event, dropdown) {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.open(dropdown);
      }
    }

    onListKeydown(event, dropdown) {
      const keys = {
        ArrowDown: () => this.moveActive(dropdown, 1),
        ArrowUp: () => this.moveActive(dropdown, -1),
        Home: () => this.moveActive(dropdown, -Infinity),
        End: () => this.moveActive(dropdown, Infinity),
        Enter: () => this.choose(dropdown, dropdown.querySelector('.option-dropdown__option--active')),
        ' ': () => this.choose(dropdown, dropdown.querySelector('.option-dropdown__option--active')),
        Escape: () => this.close({ focusField: true }),
      };

      if (keys[event.key]) {
        event.preventDefault();
        keys[event.key]();
        return;
      }
      if (event.key === 'Tab') {
        this.close();
        return;
      }
      if (event.key.length === 1) this.jumpTo(dropdown, event.key);
    }

    jumpTo(dropdown, character) {
      clearTimeout(this.typeaheadTimer);
      this.typeahead += character.toLowerCase();
      this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 500);
      const match = this.options(dropdown).find((option) =>
        option.dataset.value.toLowerCase().startsWith(this.typeahead)
      );
      this.setActive(dropdown, match);
    }
  }

  customElements.define('option-dropdowns', OptionDropdowns);
}

if (!customElements.get('buybox-status')) {
  class BuyboxStatus extends HTMLElement {
    connectedCallback() {
      const { section } = this.dataset;
      this.button = document.getElementById(`ProductSubmitButton-${section}`);
      this.price = this.button?.querySelector('.buybox-price');
      this.lowStock = readJson(document.getElementById(`low-stock-data-${section}`), {});
      this.preorder = readJson(document.getElementById(`preorder-data-${section}`), { variants: {} });
      this.picker = document.getElementById(`variant-selects-${section}`);

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId === section) this.render(data.variant);
      });

      // Repaint the stock line at once; the price follows the section fetch,
      // and unbuilt combinations never publish variant-change at all.
      this.onPickerChange = () => this.render(this.picker.currentVariant ?? null, { updatePrice: false });
      this.picker?.addEventListener('change', this.onPickerChange);
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.picker?.removeEventListener('change', this.onPickerChange);
    }

    render(variant, { updatePrice = true } = {}) {
      const text = this.querySelector('.buybox-status__text');
      const note = this.querySelector('.buybox-status__note');

      if (!variant?.available) {
        this.dataset.state = '';
        text.textContent = '';
        note.textContent = '';
        if (this.price) this.price.textContent = '';
        return;
      }

      if (this.preorder.variants?.[variant.id]) {
        this.dataset.state = 'preorder';
        text.textContent = 'PRE-ORDER';
        note.textContent = this.preorder.window ? `EST. SHIP DATE: ${this.preorder.window}` : 'SHIPS WITH THE NEXT DROP';
      } else {
        const units = this.lowStock[variant.id] ?? 0;
        this.dataset.state = units > 0 ? 'low' : 'instock';
        text.textContent = 'IN STOCK';
        note.textContent = units > 0 ? `ONLY ${units} LEFT` : '';
      }

      if (this.price && updatePrice) {
        const priceBlock = document.getElementById(`price-${this.dataset.section}`);
        const kwAll = priceBlock ? Array.from(priceBlock.querySelectorAll(
          '.price--on-sale .price__sale .price-item--last, .price:not(.price--on-sale) .price__regular .price-item--regular'
        )) : [];
        const kwHidden = (window.kwTP && window.kwTP() ? '.kw-tp-post' : '.kw-tp-pre')
          + (document.documentElement.classList.contains('kw-bmsm-live') ? ', .kw-bmsm-solo--off' : ', .kw-bmsm-solo--on');
        const current = kwAll.find((el) => !el.closest(kwHidden)) || kwAll[0];
        const amount = current?.textContent.trim();
        this.price.textContent = amount ? ` - ${amount}` : '';
      }
    }
  }

  customElements.define('buybox-status', BuyboxStatus);
}
